import type { CarePlan, EpisodeOfCare, QuestionnaireResponse } from '@medplum/fhirtypes';
import { TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import {
  getCarePlanEpisodeId,
  isActiveTreatmentPlanCarePlan,
} from '@unify/fhir-model/resources/treatment-plan-careplan';
import { getEpisodeOfCareContext } from '@unify/fhir-model/resources/questionnaire-response';
import {
  extractDiagnosesFromQuestionnaireResponse,
  type ParsedTreatmentPlanDiagnosis,
} from '../treatmentPlanDiagnosis';

const TREATMENT_PLANNING_TAG = 'treatment-planning';

export type ActiveTreatmentPlanDiagnosis = ParsedTreatmentPlanDiagnosis & {
  episodeId: string;
};

export function hasTreatmentPlanningTag(qr: QuestionnaireResponse): boolean {
  return (
    qr.meta?.tag?.some(
      (tag) => tag.system === TEMPLATE_CATEGORY_SYSTEM && tag.code === TREATMENT_PLANNING_TAG,
    ) ?? false
  );
}

export function isActiveEpisodeOfCare(eoc: Pick<EpisodeOfCare, 'status'>): boolean {
  return eoc.status === 'active' || eoc.status === 'onhold';
}

/** True when a treatment-plan QR should still count as an “active” plan for diagnosis display. */
export function isActiveTreatmentPlanQr(qr: QuestionnaireResponse): boolean {
  if (qr.status === 'stopped' || qr.status === 'entered-in-error') return false;
  return hasTreatmentPlanningTag(qr);
}

export function getEpisodeIdFromQr(qr: QuestionnaireResponse): string | undefined {
  return getEpisodeOfCareContext(qr)?.reference?.replace(/^EpisodeOfCare\//, '') || undefined;
}

/**
 * Collect diagnoses from the latest active treatment-plan QR per active episode, deduped by ICD-10 code.
 */
export function collectActiveTreatmentPlanDiagnoses(input: {
  episodes: EpisodeOfCare[];
  responses: QuestionnaireResponse[];
}): ActiveTreatmentPlanDiagnosis[] {
  const activeEpisodeIds = new Set(
    input.episodes.filter(isActiveEpisodeOfCare).map((e) => e.id).filter((id): id is string => Boolean(id)),
  );
  if (activeEpisodeIds.size === 0) return [];

  const byEpisode = new Map<string, QuestionnaireResponse>();
  const sorted = [...input.responses]
    .filter(isActiveTreatmentPlanQr)
    .sort((a, b) => (b.authored ?? '').localeCompare(a.authored ?? ''));

  for (const qr of sorted) {
    const episodeId = getEpisodeIdFromQr(qr);
    if (!episodeId || !activeEpisodeIds.has(episodeId)) continue;
    if (!byEpisode.has(episodeId)) {
      byEpisode.set(episodeId, qr);
    }
  }

  const seenCodes = new Set<string>();
  const out: ActiveTreatmentPlanDiagnosis[] = [];
  for (const [episodeId, qr] of byEpisode) {
    for (const dx of extractDiagnosesFromQuestionnaireResponse(qr)) {
      const key = dx.code.toUpperCase();
      if (seenCodes.has(key)) continue;
      seenCodes.add(key);
      out.push({ ...dx, episodeId });
    }
  }
  return out;
}

export function collectActiveTreatmentPlanDiagnosesFromCarePlans(input: {
  episodes: EpisodeOfCare[];
  carePlans: CarePlan[];
}): ActiveTreatmentPlanDiagnosis[] {
  const activeEpisodeIds = new Set(
    input.episodes.filter(isActiveEpisodeOfCare).map((e) => e.id).filter((id): id is string => Boolean(id)),
  );
  if (activeEpisodeIds.size === 0) return [];

  const byEpisode = new Map<string, CarePlan>();
  const sorted = [...input.carePlans]
    .filter(isActiveTreatmentPlanCarePlan)
    .sort((a, b) => (b.created ?? '').localeCompare(a.created ?? ''));

  for (const plan of sorted) {
    const episodeId = getCarePlanEpisodeId(plan);
    if (!episodeId || !activeEpisodeIds.has(episodeId)) continue;
    if (!byEpisode.has(episodeId)) byEpisode.set(episodeId, plan);
  }

  const seenCodes = new Set<string>();
  const out: ActiveTreatmentPlanDiagnosis[] = [];
  for (const [episodeId, plan] of byEpisode) {
    for (const addr of plan.addresses ?? []) {
      const raw = addr.display ?? '';
      const code = raw.split(/\s*[-–—]\s*/)[0]?.trim();
      if (!code) continue;
      const key = code.toUpperCase();
      if (seenCodes.has(key)) continue;
      seenCodes.add(key);
      out.push({
        code: key,
        display: raw.includes(' - ') ? raw.split(/\s*[-–—]\s*/).slice(1).join(' - ').trim() : raw,
        raw,
        episodeId,
      });
    }
  }
  return out;
}

/** Use QR diagnoses only for active episodes that do not yet have an active CarePlan. */
export function mergeActiveTreatmentPlanDiagnosisFallback(
  carePlanDiagnoses: ActiveTreatmentPlanDiagnosis[],
  qrDiagnoses: ActiveTreatmentPlanDiagnosis[],
): ActiveTreatmentPlanDiagnosis[] {
  const coveredEpisodeIds = new Set(carePlanDiagnoses.map((diagnosis) => diagnosis.episodeId));
  const seenCodes = new Set(carePlanDiagnoses.map((diagnosis) => diagnosis.code.toUpperCase()));
  const merged = [...carePlanDiagnoses];
  for (const diagnosis of qrDiagnoses) {
    const code = diagnosis.code.toUpperCase();
    if (coveredEpisodeIds.has(diagnosis.episodeId) || seenCodes.has(code)) continue;
    seenCodes.add(code);
    merged.push(diagnosis);
  }
  return merged;
}
